import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, Search, User, MapPin, X, LogOut, Heart, Settings as SettingsIcon, LifeBuoy, ChevronDown } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/contexts/AuthContext";
import { useProvince } from "@/contexts/ProvinceContext";
import api from "@/lib/api";
import NotificationBell from "@/components/layout/NotificationBell";

export default function Header({ onToggleSidebar }) {
  const { user, logout, isAdmin } = useAuth();
  const { provinces, selected, choose } = useProvince();
  const nav = useNavigate();
  const [showProv, setShowProv] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [provQ, setProvQ] = useState("");

  const filteredProv = provinces.filter((p) => p.name.toLocaleLowerCase("tr").includes(provQ.toLocaleLowerCase("tr")));

  const doSearch = async (q) => {
    setSearchQ(q);
    if (q.length < 2) return setSearchResults(null);
    try {
      const r = await api.get(`/search?q=${encodeURIComponent(q)}${selected ? `&province_id=${selected.id}` : ""}`);
      setSearchResults(r.data);
    } catch {}
  };

  return (
    <header className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm">
      {/* Top thin bar */}
      <div className="bg-[#0b0b0d] text-white text-xs">
        <div className="max-w-7xl mx-auto px-4 py-1.5 flex items-center justify-between">
          <div className="hidden md:flex items-center gap-3">
            <span className="opacity-80">81 İlden Tek Gerçek</span>
            <span className="opacity-40">•</span>
            <span className="opacity-80">{new Date().toLocaleDateString("tr-TR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</span>
          </div>
          <div className="flex items-center gap-3 ml-auto">
            <Link to="/tv" className="hover:text-[#e11d2a] flex items-center gap-1"><span className="inline-block w-2 h-2 bg-[#e11d2a] rounded-full animate-pulse"></span> CANLI YAYIN</Link>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 h-16 flex items-center gap-3">
        <button data-testid="header-hamburger" onClick={onToggleSidebar} className="p-2 hover:bg-gray-100 rounded-md" aria-label="Menu">
          <Menu className="w-6 h-6" />
        </button>

        {/* Province */}
        <div className="relative hidden md:block">
          <button data-testid="province-button" onClick={() => setShowProv(!showProv)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-gray-200 hover:border-[#e11d2a] text-sm font-medium">
            <MapPin className="w-4 h-4 text-[#e11d2a]" />
            <span data-testid="province-name">{selected?.name || "Genel"}</span>
            <ChevronDown className="w-3.5 h-3.5" />
          </button>
          {showProv && (
            <div className="absolute left-0 top-full mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-xl p-2 max-h-96 overflow-y-auto z-50">
              <input value={provQ} onChange={(e) => setProvQ(e.target.value)} placeholder="İl ara..." className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm mb-2" />
              <div className="grid grid-cols-2 gap-1">
                {filteredProv.map((p) => (
                  <button key={p.id} data-testid={`province-opt-${p.slug}`} onClick={() => { choose(p); setShowProv(false); nav("/"); }} className={`text-left text-sm px-2 py-1.5 rounded hover:bg-gray-100 ${selected?.id === p.id ? "bg-[#e11d2a] text-white hover:bg-[#e11d2a]" : ""}`}>
                    {p.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Logo center */}
        <div className="flex-1 flex justify-center">
          <Link to="/" data-testid="header-logo">
            <Logo size="md" />
          </Link>
        </div>

        {/* Right */}
        <div className="flex items-center gap-1">
          {/* Mobile province — show name like desktop */}
          <button
            onClick={() => setShowProv(!showProv)}
            className="md:hidden flex items-center gap-1 px-2 py-1.5 hover:bg-gray-100 rounded-md text-sm font-medium max-w-[110px]"
            aria-label="İl Seç"
            data-testid="province-button-mobile"
          >
            <MapPin className="w-4 h-4 text-[#e11d2a] shrink-0" />
            <span className="truncate">{selected?.name || "Genel"}</span>
          </button>
          <NotificationBell />
          {/* Member */}
          <div className="relative">
            <button data-testid="header-member" onClick={() => setShowMenu(!showMenu)} className="p-2 hover:bg-gray-100 rounded-md" aria-label="User">
              <User className="w-5 h-5" />
            </button>
            {showMenu && (
              <div className="absolute right-0 top-full mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-xl py-1 z-50">
                {user ? (
                  <>
                    <div className="px-3 py-2 border-b border-gray-100">
                      <div className="font-medium text-sm">{user.first_name || user.username}</div>
                      <div className="text-xs text-gray-500 truncate">{user.email}</div>
                    </div>
                    <MenuLink to="/profil" icon={<User className="w-4 h-4" />} label="Profilim" onClick={() => setShowMenu(false)} />
                    <MenuLink to="/kaydettiklerim" icon={<Heart className="w-4 h-4" />} label="Kaydettiklerim" onClick={() => setShowMenu(false)} />
                    <MenuLink to="/ayarlar" icon={<SettingsIcon className="w-4 h-4" />} label="Ayarlarım" onClick={() => setShowMenu(false)} />
                    <MenuLink to="/destek" icon={<LifeBuoy className="w-4 h-4" />} label="Destek" onClick={() => setShowMenu(false)} />
                    {isAdmin && <MenuLink to="/admin" icon={<SettingsIcon className="w-4 h-4" />} label="Admin Panel" onClick={() => setShowMenu(false)} />}
                    <button data-testid="header-logout" onClick={() => { logout(); setShowMenu(false); nav("/"); }} className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm flex items-center gap-2 border-t border-gray-100">
                      <LogOut className="w-4 h-4" /> Çıkış
                    </button>
                  </>
                ) : (
                  <>
                    <MenuLink to="/giris" icon={<User className="w-4 h-4" />} label="Giriş Yap" onClick={() => setShowMenu(false)} />
                    <MenuLink to="/kayit" icon={<User className="w-4 h-4" />} label="Üye Ol" onClick={() => setShowMenu(false)} />
                  </>
                )}
              </div>
            )}
          </div>
          {/* Search */}
          <button data-testid="header-search" onClick={() => setShowSearch(!showSearch)} className="p-2 hover:bg-gray-100 rounded-md" aria-label="Search">
            <Search className="w-5 h-5" />
          </button>
        </div>
      </div>
      {/* Search panel */}
      {showSearch && (
        <div className="border-t border-gray-200 bg-white">
          <div className="max-w-7xl mx-auto px-4 py-3">
            <div className="flex items-center gap-2">
              <Search className="w-5 h-5 text-gray-400" />
              <input data-testid="search-input" autoFocus value={searchQ} onChange={(e) => doSearch(e.target.value)} placeholder="Haber, video, etkinlik veya köşe yazarı ara..." className="flex-1 outline-none text-sm py-2" />
              <button onClick={() => { setShowSearch(false); setSearchResults(null); setSearchQ(""); }} className="p-1 hover:bg-gray-100 rounded"><X className="w-5 h-5" /></button>
            </div>
            {searchResults && (
              <div className="mt-2 max-h-96 overflow-y-auto grid md:grid-cols-2 gap-3">
                {[["news", "Haberler", "/haber/"], ["columns", "Yazılar", "/kose-yazilari/"], ["videos", "Videolar", "/video/"], ["events", "Etkinlikler", "/etkinlikler/"]].map(([k, label, base]) => (
                  (searchResults[k]?.length > 0) && (
                    <div key={k}>
                      <div className="text-xs uppercase text-gray-500 font-semibold mb-1">{label}</div>
                      <div className="space-y-1">
                        {searchResults[k].slice(0, 5).map((it) => (
                          <Link key={it.id} to={`${base}${it.slug || it.id}`} onClick={() => { setShowSearch(false); setSearchResults(null); }} className="block px-2 py-1.5 rounded hover:bg-gray-50 text-sm">{it.title || it.name}</Link>
                        ))}
                      </div>
                    </div>
                  )
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {/* Mobile province dropdown */}
      {showProv && (
        <div className="md:hidden border-t border-gray-200 bg-white">
          <div className="max-w-7xl mx-auto px-4 py-2">
            <input value={provQ} onChange={(e) => setProvQ(e.target.value)} placeholder="İl ara..." className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm mb-2" />
            <div className="grid grid-cols-3 gap-1 max-h-64 overflow-y-auto">
              {filteredProv.map((p) => (
                <button key={p.id} onClick={() => { choose(p); setShowProv(false); nav("/"); }} className={`text-left text-sm px-2 py-1.5 rounded hover:bg-gray-100 ${selected?.id === p.id ? "bg-[#e11d2a] text-white" : ""}`}>
                  {p.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

function MenuLink({ to, icon, label, onClick }) {
  return (
    <Link to={to} onClick={onClick} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 text-sm">
      {icon} {label}
    </Link>
  );
}
